import { PrintButton } from "@dana/Components/PrintButton/PrintButton";
import StandardReturnsOrderRecepients from "@dana/Components/StandardReturnsOrderStatus/StandardReturnsOrderRecepients";
import StyledTable from "@dana/Components/StyledTable/StyledTable";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import {
    OrderInfo,
    RmaAdditionalDetails,
    SalseOrgDetails,
    StandardReturnOrderDetails,
} from "@dana/Services/StandardReturnDetailsService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import exportStandardReturnOrder from "@dana/Store/Data/StandardReturnsStatus/Handlers/ExportStandardReturnOrder";
import { SystemListValuesModel } from "@dana/Types/ApiModels";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import Checkbox from "@insite/mobius/Checkbox";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface AnnualReturnsRMADetailsViewProps {
    // Define your props here
}

// --- Styles Interfaces and Objects ---
export interface AnnualReturnsRMADetailsViewStyles {
    invoiceContainer?: InjectableCss;
}

export const AnnualReturnsRMADetailsViewStyles: AnnualReturnsRMADetailsViewStyles = {
    invoiceContainer: {
        css: css`
            font-size: ${fontSizes.medium};
            line-height: ${lineHeights.medium};

            .invoice-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 16px;
                padding: 16px 0;
            }

            .border-box {
                border: 1px solid ${themeBackgrounds.lightGray};
                padding: 16px;
            }

            table {
                width: 100%;

                .price-cell {
                    text-align: right;
                }
            }

            td.company-logo {
                width: 200px;
            }

            td.reprint-text {
                width: 200px;
                text-align: right;
                vertical-align: top;
            }

            .address,
            address {
                font-style: normal;
            }

            .invoice-address-table {
                .section-left {
                    width: 45%;
                    padding-right: 30px;
                }

                .section-right {
                    width: 55%;
                }
            }

            .delivery-information-table {
                .invoice_card {
                    height: fit-content;
                }
            }

            .reason-disposition-table {
                ul {
                    padding-left: 24px;
                    margin-top: 16px;
                }
                @media print {
                    td {
                        padding-left: 4px;
                        padding-right: 4px;
                    }
                }
            }

            .line-item-table-wrapper {
                overflow: auto;

                @media print {
                    overflow: visible;
                    padding-left: 5px;
                    padding-right: 5px;
                    .line-item-table {
                        width: 100%;
                        border-collapse: collapse;
                        word-wrap: break-word;
                        border: 1px solid ${themeBackgrounds.lightGray};
                        margin-top: 8px;
                        th,
                        td {
                            white-space: normal;
                            word-wrap: break-word;
                            overflow-wrap: break-word;
                            padding: 8px;
                            text-align: left;
                            vertical-align: top;
                            &:first-child {
                                padding-left: 2px;
                            }
                            &:last-child {
                                padding-right: 5px;
                            }
                        }
                        thead th {
                            color: ${themeBackgrounds.black};
                        }
                        thead th,
                        tbody td {
                            border: none;
                            border-bottom: 1px solid ${themeBackgrounds.lightGray};
                        }
                        thead,
                        tbody,
                        tfoot {
                            th,
                            td {
                                font-size: 0.875rem;
                                line-height: 1.3125rem;
                                padding-top: 2px;
                                padding-bottom: 2px;
                            }
                        }
                        tfoot {
                            td {
                                border: none;
                            }
                        }
                        .qtyHead,
                        .priceHead,
                        .depositHead,
                        .amountHead,
                        .qtyCell,
                        .priceCell,
                        .depositCell,
                        .amountCell {
                            text-align: center;
                        }
                        .descHead,
                        .descCell {
                            width: 300px;
                        }
                        .qtyHead,
                        .qtyCell {
                            width: 75px;   
                        }
                        .itemHead,
                        .itemCell {
                            width: 75px;     
                        }
                        .priceHead,
                        .priceCell {
                            width: 62px;   
                        }
                        .depositHead,
                        .depositCell {
                            width: 130px;
                            word-break: break-word; 
                        }
                        .amountHead,
                        .amountCell {
                            width: 140px;
                            word-break: break-word;
                        }
                        .printRightAlign {
                            text-align: right;
                        }
                        thead,
                        tbody {
                            tr, th, td {
                                page-break-inside: avoid !important;
                            }
                        }
                        tfoot {
                            display: table-row-group;
                        }
                    }
                }
            }

            .line-item-table {
                thead th,
                tbody td,
                tfoot td {
                    font-size: ${fontSizes.medium};
                    line-height: ${lineHeights.medium};
                }
            }

            .address-table {
                .invoice_card {
                    height: 150px;
                }
            }

            .company-address {
                h3 {
                    font-weight: ${fontWeights.semiBold};
                }
            }

            .printtype-text {
                font-size: ${fontSizes.large};
                line-height: ${lineHeights.large};
                font-weight: ${fontWeights.semiBold};
                color: ${themeColors.primary.danaBlue};
                text-transform: uppercase;
                margin-bottom: 16px;
            }

            .print-text {
                font-weight: ${fontWeights.semiBold};
                text-transform: uppercase;
            }

            .header-table {
                margin-bottom: 24px;
            }

            .freight-collect-information {
                page-break-before: always;
                margin-top: 32px;

                .info-title {
                    width: 400px;
                }
            }

            .break-spaces {
                white-space: break-spaces;
            }

            .pricing-box {
                > div {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin: 8px 0;
                    @media print {
                        margin: 2px 0;
                    }
                }
            }

            .cutting-card-wrapper {
                font-size: 23px;
                font-weight: 600;
                max-width: 850px;
                margin: 32px auto;

                &.first-card {
                    page-break-before: always;
                }

                .cutting-card {
                    border: 2px dashed black;
                    padding: 8px;
                    margin-top: 35px;
                    position: relative;

                    &::before {
                        content: "✂";
                        position: absolute;
                        font-size: 60px;
                        top: -45px;
                        right: 0px;
                        transform: rotate(140deg);

                        @media only screen and (max-width: 768px) {
                            font-size: 30px;
                            top: -30px;
                        }
                    }
                }

                .cutting-card_logo {
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .cutting-card-body {
                    margin: 16px 0;
                }

                .cutting-card-info {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    margin-bottom: 16px;
                }
            }

            @media only screen and (max-width: 768px) {
                .header-table,
                .invoice-address-table,
                .freight-collect-information {
                    table,
                    tbody,
                    td,
                    tr {
                        display: block;
                    }
                }

                .freight-collect-information .freight-collect-address-table {
                    display: table;

                    tr {
                        display: table-row;
                    }
                    tbody {
                        display: table-row-group;
                    }

                    td {
                        display: table-cell;
                    }
                }

                .invoice-address-table {
                    .section-left,
                    .section-right {
                        width: 100%;
                    }

                    .section-left {
                        padding-right: 0;
                    }
                }

                td.reprint-text {
                    text-align: left;
                }
            }
            @media print {
                font-size: 0.875rem;
                line-height: 1.3125rem;
            }  
        `,
    },
};

const styles = AnnualReturnsRMADetailsViewStyles;
const mapStateToProps = (state: ApplicationStateDana) => ({
    isLoading: state.standardReturnsDetailsState.isLoading,
    language: state.context.session.language,
    logo: state.standardReturnsDetailsState.logo,
    salseOrgDetails: state.standardReturnsDetailsState.salseOrgDetails,
    orderModel: state.standardReturnsDetailsState.orderModel,
    rmaAdditionalDetails: state.standardReturnsDetailsState.rmaAdditionalDetails,
    dispositionPreferences: state.standardReturnsDetailsState.dispositionPreferences,
});

const mapDispatchToProps = {
    exportStandardReturnOrder,
};
type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;
// --- Component Definition ---
const AnnualReturnsRMADetailsView: FC<Props> = ({
    language,
    isLoading,
    orderModel,
    logo,
    salseOrgDetails,
    rmaAdditionalDetails,
    dispositionPreferences,
}) => {
    const modalContentRef = useRef<HTMLDivElement | null>(null);
    const [salesOrganizationDetails, setSalseOrgDetails] = useState<SalseOrgDetails>();
    const [standardReturnOrderDetails, setStandardReturnOrderDetails] = useState<StandardReturnOrderDetails>();
    const [rmaAdditionalDetail, setRmaAdditionalDetails] = useState<RmaAdditionalDetails>();
    const [dispositionPreferenceList, setDispositionPreferenceList] = useState<SystemListValuesModel[]>();

    useEffect(() => {
        setSalseOrgDetails(salseOrgDetails);
        setStandardReturnOrderDetails(orderModel);
        setRmaAdditionalDetails(rmaAdditionalDetails);
        setDispositionPreferenceList(dispositionPreferences);
    }, [salseOrgDetails, orderModel, rmaAdditionalDetails, dispositionPreferences]);
    
    if (isLoading) {
        return <LoadingSpinner></LoadingSpinner>;
    }

    return (
        <StyledWrapper className="invoice-container" {...styles.invoiceContainer} ref={modalContentRef}>
            <StyledWrapper className="invoice-actions text-right">
                {standardReturnOrderDetails && modalContentRef.current !== null && (
                    <StandardReturnsOrderRecepients
                        orderType="StandardReturn"
                        orderData={standardReturnOrderDetails}
                        contentRef={modalContentRef}
                    />
                )}

                <PrintButton label={translate("Print Payslip")} targetRef={modalContentRef} />
            </StyledWrapper>

            {/* Header */}
            <table className="header-table">
                <tbody>
                    <tr>
                        <td className="company-logo">
                            {/* Replace src with {websiteData.companyLogoImagePath} */}
                            <img src={logo} alt="Company Logo" className="company-logo" />
                        </td>
                        <td className="company-address">
                            <div className="address">
                                <h3>{salesOrganizationDetails?.orgName}</h3>
                                <p>
                                    {salesOrganizationDetails?.orgAddress}
                                    {(
                                        salesOrganizationDetails?.city ||
                                        salesOrganizationDetails?.state ||
                                        salesOrganizationDetails?.zipCode ||
                                        salesOrganizationDetails?.country
                                    ) && (
                                            <>
                                                <br />
                                                {[
                                                    [salesOrganizationDetails?.city, salesOrganizationDetails?.state]
                                                        .filter(Boolean)
                                                        .join(", "),
                                                    [salesOrganizationDetails?.zipCode, salesOrganizationDetails?.country]
                                                        .filter(Boolean)
                                                        .join(" "),
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            </>
                                        )}

                                    {salesOrganizationDetails?.siteName && (
                                        <>
                                            <br />
                                            {salesOrganizationDetails.siteName}
                                        </>
                                    )}
                                </p>
                            </div>
                        </td>
                        <td className="reprint-text">
                            <Typography forwardAs="p" className="printtype-text">
                                {translate("Annual Returns")}
                            </Typography>
                            <div>{new Date().toLocaleString()}</div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table className="invoice-address-table">
                <tbody>
                    <tr>
                        <td valign="top" className="section-left">
                            {/* Sold To and Ship To */}
                            <table className="address-table">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {" "}
                                                    {translate("Sold-To Party")}: {standardReturnOrderDetails?.billTo}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <div className="address">
                                                        <p>
                                                            {standardReturnOrderDetails?.billName}
                                                            {standardReturnOrderDetails?.billAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.billAddress1}
                                                                </>
                                                            )}

                                                            {(
                                                                standardReturnOrderDetails?.billCity ||
                                                                standardReturnOrderDetails?.billState ||
                                                                standardReturnOrderDetails?.billZipcode
                                                            ) && (
                                                                    <>
                                                                        <br />
                                                                        {[
                                                                            standardReturnOrderDetails?.billCity,
                                                                            standardReturnOrderDetails?.billState,
                                                                            standardReturnOrderDetails?.billZipcode,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" ")}
                                                                    </>
                                                                )}

                                                            {standardReturnOrderDetails?.billCountry && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.billCountry}
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">
                                                    {translate("Ship-from Party")}: {standardReturnOrderDetails?.shipTo}
                                                </div>
                                                <div className="invoice_card_body">
                                                    <div className="address">
                                                        <p>
                                                            {standardReturnOrderDetails?.shipName}
                                                            {standardReturnOrderDetails?.shipAddress1 && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.shipAddress1}
                                                                </>
                                                            )}
                                                            {(
                                                                standardReturnOrderDetails?.shipCity ||
                                                                standardReturnOrderDetails?.shipState ||
                                                                standardReturnOrderDetails?.shipZipcode
                                                            ) && (
                                                                    <>
                                                                        <br />
                                                                        {[
                                                                            standardReturnOrderDetails?.shipCity,
                                                                            standardReturnOrderDetails?.shipState,
                                                                            standardReturnOrderDetails?.shipZipcode,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(" ")}
                                                                    </>
                                                                )}
                                                            {standardReturnOrderDetails?.shipCountry && (
                                                                <>
                                                                    <br />
                                                                    {standardReturnOrderDetails.shipCountry}
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">{translate("Ship Return to")}</div>
                                                <div className="invoice_card_body">
                                                    <div className="address">
                                                        <p>
                                                            {translate(" Dana Corporation")}
                                                            {salesOrganizationDetails?.orgAddress && (
                                                                <>
                                                                    <br />
                                                                    {salesOrganizationDetails.orgAddress}
                                                                </>
                                                            )}
                                                            {(
                                                                salesOrganizationDetails?.city ||
                                                                salesOrganizationDetails?.state ||
                                                                salesOrganizationDetails?.zipCode
                                                            ) && (
                                                                <>
                                                                    <br />
                                                                    {[
                                                                        salesOrganizationDetails?.city,
                                                                        salesOrganizationDetails?.state,
                                                                        salesOrganizationDetails?.zipCode,
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(", ")}
                                                                </>
                                                            )}
                                                            {salesOrganizationDetails?.country && (
                                                                <>
                                                                    <br />
                                                                    {salesOrganizationDetails.country}
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                        <td valign="top" className="section-right">
                            {/* Delivery Information */}
                            <table className="delivery-information-table">
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="invoice_card">
                                                <div className="invoice_card_header">{translate("Information")}</div>
                                                <div className="invoice_card_body">
                                                    <table>
                                                        <tbody>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Return Authorization No.")}
                                                                </td>
                                                                <td> {standardReturnOrderDetails?.documentNumber}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Document Date")}
                                                                </td>
                                                                <td>
                                                                    {standardReturnOrderDetails?.documentDate
                                                                        ? getLocalizedDateTime({
                                                                            dateTime: new Date(
                                                                                standardReturnOrderDetails?.documentDate,
                                                                            ),
                                                                            language,
                                                                        })
                                                                        : ""}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Customer No.")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.billTo}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Currency")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.currency}</td>
                                                            </tr>
                                                            {/* <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Incoterms")}
                                                                </td>
                                                                <td></td>
                                                            </tr> */}
                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Customer PO")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.poNumber}</td>
                                                            </tr>

                                                            <tr>
                                                                <td className="font-semibold">
                                                                    {translate("Dealer Code")}
                                                                </td>
                                                                <td>{standardReturnOrderDetails?.dealerCode}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Reason and Disposition Preference */}

            <table className="reason-disposition-table">
                <tbody>
                    <tr>
                        <td>
                            <StyledWrapper className="border-box">
                                <Typography as="p">
                                    {translate(
                                        "If any of these parts are deemed non-returnable for credi (not on the RMA, damaged, rusted, nicks, etc.), please indicate your disposition preference.",
                                    )}
                                </Typography>
                                <ul>
                                    {dispositionPreferenceList
                                        ?.filter(
                                            (systemList: SystemListValuesModel) =>
                                                systemList.name !== "CONTACT_CUSTOMER_OPERATIONS",
                                        )
                                        .map((systemList: SystemListValuesModel) => (
                                            <li key={systemList.id}>
                                                <strong>
                                                    {systemList.name === rmaAdditionalDetail?.disposition
                                                        ? "[✔]"
                                                        : "[ ]"}{" "}
                                                    {systemList.description}
                                                </strong>
                                            </li>
                                        ))}
                                </ul>
                            </StyledWrapper>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Line Items table */}
            <StyledWrapper className="line-item-table-wrapper">
                <StyledTable className="line-item-table">
                    <thead>
                        <tr>
                            <th className="itemHead">{translate("Item")}</th>
                            <th className="descHead">{translate("Material/Description")}</th>
                            <th className="qtyHead">{translate("Quantity/UOM")}</th>
                            <th className="priceHead">{translate(`Price (${standardReturnOrderDetails?.currency})`)}</th>
                            <th className="depositHead">{translate(`Unit Core Deposit (${standardReturnOrderDetails?.currency})`)}</th>
                            <th className="amountHead">{translate(`Extended Amount (${standardReturnOrderDetails?.currency})`)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {standardReturnOrderDetails?.orderInfo.map((orderline: OrderInfo) => {
                            return (
                                    <tr key={orderline.lineNumber}>
                                        <td className="itemCell">{orderline.lineNumber}</td>
                                        <td className="break-spaces descCell">
                                            <div>
                                                {orderline.partNumber}
                                                <br />
                                                {orderline.partDescription}
                                                <br />
                                                {translate("Customer Part No")}: {orderline.customerItemNumber}
                                                <br />
                                                {translate("Country Of Origin")}: {orderline.countryofOrigin}
                                                <br />
                                                {translate("Custom Commodity Code")}: {orderline.hsCode}
                                                <br />
                                            </div>
                                        </td>
                                        <td className="qtyCell"> {orderline.quantityOrdered} EA</td>
                                        <td className="price-cell priceCell">{formatDecimal(orderline.baseprice)} </td>
                                        <td className="price-cell depositCell">{formatDecimal(orderline.extendedAmount)}</td>
                                        <td className="price-cell amountCell">
                                            {formatDecimal(
                                                orderline.baseprice * orderline.quantityOrdered +
                                                orderline.extendedAmount
                                            )}
                                        </td>
                                    </tr>
                            );
                        })}
                    </tbody>

                    {(() => {
                        if (!standardReturnOrderDetails?.orderInfo) {
                            return null;
                        }

                        const restockingFees = standardReturnOrderDetails?.orderInfo.reduce(
                            (sum, item) => sum + (item.restockingFees || 0),
                            0,
                        );
                        const totalNetAmount = standardReturnOrderDetails?.orderInfo.reduce(
                            (sum, item) => sum + (item.baseprice * item.quantityOrdered + item.extendedAmount || 0),
                            0,
                        );
                        const finalAmount = totalNetAmount - restockingFees;

                        return (
                            <tfoot>
                                <tr>
                                    <td colSpan={4}></td>
                                    <td colSpan={2} rowSpan={2}>
                                        <div className="pricing-box">
                                            <div>
                                                <strong>{translate("Items Total:")}</strong>
                                                <p>{formatDecimal(totalNetAmount)}</p>
                                            </div>
                                            <div>
                                                <strong>{translate("Re-stocking Fees:")}</strong>
                                                <p>{formatDecimal(restockingFees)}</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr></tr>
                                <tr></tr>
                                <tr>
                                    <td colSpan={4}></td>
                                    <td colSpan={1}>
                                        <strong>{translate("Final Amount:")}</strong>
                                    </td>
                                    <td className="price-cell printRightAlign">{formatDecimal(finalAmount)}</td>
                                </tr>
                            </tfoot>
                        );
                    })()}
                </StyledTable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AnnualReturnsRMADetailsView);
